import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Trash2, Undo2 } from 'lucide-react'
import { formatBalance } from '@/lib/format-number'
import { cn } from '@/lib/utils'
import type { BaccaratBetPlace, BaccaratBetResult, BaccaratHandWinner, BaccaratPlacedChip, BaccaratPlayingCard, BaccaratRoundStatus, BaccaratSuit } from '@/types/baccarat'
import { AssetManager } from '../core/AssetManager'
import { Chip } from './Chip'
import { ResultModal } from './ResultModal'

const DEAL_STAGGER_MS = 140
const DEAL_DURATION_MS = 480
const SPOT_FLASH_MS = 500
const CHIP_DISPLAY_SIZE = 28

const BACCARAT_PAYOUT_MULTIPLIERS = {
	Player: 1.94,
	Banker: 1.89,
	Tie: 8.74,
	PPair: 11.65,
	BPair: 11.65,
} as const

const suitAssetKey = {
	Hearts: 'suitHearts',
	Diamonds: 'suitDiamonds',
	Clubs: 'suitClubs',
	Spades: 'suitSpades',
} as const

const BETTING_SPOTS: Array<{
	place: BaccaratBetPlace
	label: string
	tall: boolean
	className?: string
}> = [
	{ place: 'Player', label: 'PLAYER', tall: true },
	{ place: 'Tie', label: 'TIE', tall: true },
	{ place: 'Banker', label: 'BANKER', tall: true },
	{ place: 'PPair', label: 'PLAYER PAIR', tall: false, className: 'col-start-1' },
	{ place: 'BPair', label: 'BANKER PAIR', tall: false, className: 'col-start-3' },
]

function isRedSuit(suit: BaccaratSuit) {
	return suit === 'Hearts' || suit === 'Diamonds'
}

function SuitImage({ suit, className }: { suit: BaccaratSuit; className?: string }) {
	return <img src={AssetManager.getImageUrl(suitAssetKey[suit])} alt='' draggable={false} className={cn('pointer-events-none select-none object-contain', className)} />
}

function CardFace({ card }: { card: BaccaratPlayingCard }) {
	const red = isRedSuit(card.suit)
	const ink = red
		? 'linear-gradient(180deg, hsl(var(--destructive)) 0%, hsl(var(--destructive)/0.8) 55%, hsl(var(--destructive)/0.5) 100%)'
		: 'linear-gradient(180deg, hsl(var(--card)) 0%, hsl(var(--background)) 50%, hsl(var(--secondary)) 100%)'

	return (
		<div
			className='relative flex h-full w-full flex-col overflow-hidden rounded-[inherit]'
			style={{
				background: 'linear-gradient(180deg, hsl(var(--foreground)) 0%, hsl(var(--foreground)/0.98) 48%, hsl(var(--foreground)/0.94) 100%)',
				boxShadow: 'inset 0 1px 0 hsl(var(--foreground)/0.85), inset 0 -1px 0 hsl(var(--background)/0.08)',
			}}
		>
			<div className='absolute left-[6%] top-[5%] z-10 flex flex-col items-center leading-none'>
				<span
					className='text-[1.25em] font-bold md:text-[1.75em]'
					style={{
						backgroundImage: ink,
						WebkitBackgroundClip: 'text',
						backgroundClip: 'text',
						color: 'transparent',
					}}
				>
					{card.rank}
				</span>
			</div>

			<div className='absolute inset-0 flex items-center justify-center'>
				<SuitImage suit={card.suit} className='h-[50%] w-[50%] drop-shadow-[0_1px_2px_hsl(var(--background)/0.18)]' />
			</div>
		</div>
	)
}

function PlayingCardView({
	card,
	className = '',
	style,
	entering = false,
	enterDelayMs = 0,
	isBanker = false,
	onEntered,
}: {
	card?: BaccaratPlayingCard
	className?: string
	style?: CSSProperties
	entering?: boolean
	enterDelayMs?: number
	isBanker?: boolean
	onEntered?: () => void
}) {
	const isHidden = !card
	const [placed, setPlaced] = useState(!entering)
	const enteredRef = useRef(false)
	const onEnteredRef = useRef(onEntered)
	onEnteredRef.current = onEntered

	useEffect(() => {
		if (!entering) {
			setPlaced(true)
			return
		}

		enteredRef.current = false
		setPlaced(false)

		const placeTimer = window.setTimeout(() => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => setPlaced(true))
			})
		}, enterDelayMs)
		const doneTimer = window.setTimeout(
			() => {
				if (enteredRef.current) {
					return
				}
				enteredRef.current = true
				onEnteredRef.current?.()
			},
			enterDelayMs + DEAL_DURATION_MS + 40,
		)

		return () => {
			window.clearTimeout(placeTimer)
			window.clearTimeout(doneTimer)
		}
	}, [entering, enterDelayMs])

	const dealFromStyle: CSSProperties = {
		right: isBanker ? '4%' : '6%',
		top: isBanker ? '-30%' : '-220%',
		transform: 'translate(30%, -40%) scale(0.88) rotate(-12deg)',
		opacity: 0,
		zIndex: 30,
		transitionDelay: '0ms',
	}

	const resolvedStyle: CSSProperties = {
		perspective: '1000px',
		aspectRatio: '3 / 4.6',
		transition: `top ${DEAL_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), right ${DEAL_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), transform ${DEAL_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${DEAL_DURATION_MS}ms ease`,
		...(placed ? style : dealFromStyle),
	}

	return (
		<div className='absolute mx-1 w-[7%] min-w-[40px] rounded-sm md:w-[9%] md:min-w-[50px]' style={resolvedStyle}>
			<div
				className='relative h-full w-full rounded-sm shadow-[0_2px_6px_hsl(var(--background)/0.45)]'
				style={{
					transformStyle: 'preserve-3d',
					transform: isHidden ? 'rotateY(180deg)' : 'rotateY(0deg)',
					transition: 'transform 0.45s ease-in-out',
					transitionDelay: placed && !isHidden ? '80ms' : '0ms',
				}}
			>
				<div
					className={cn('absolute inset-0 overflow-hidden rounded-sm outline outline-[4px] md:rounded-sm md:outline-[6px]', className)}
					style={{
						backfaceVisibility: 'hidden',
						WebkitBackfaceVisibility: 'hidden',
						transform: 'rotateY(0deg)',
					}}
				>
					{card ? <CardFace card={card} /> : null}
				</div>

				<div
					className='absolute inset-0 overflow-hidden rounded-sm md:rounded-sm'
					style={{
						backfaceVisibility: 'hidden',
						WebkitBackfaceVisibility: 'hidden',
						transform: 'rotateY(180deg)',
					}}
				>
					<img src={AssetManager.getImageUrl('cardBack')} alt='' className='h-full w-full object-cover' draggable={false} />
				</div>
			</div>
		</div>
	)
}

function DeckStack() {
	return (
		<div className='absolute right-[2.7%] top-[7.4%] w-[7%] md:w-[9%]'>
			{Array.from({ length: 5 }, (_, key) => (
				<PlayingCardView
					key={key}
					style={{
						top: '10%',
						right: '3%',
						width: '100%',
						transform: `translate(0px,${-key * 2}px)`,
					}}
				/>
			))}
		</div>
	)
}

function HandPanel({
	hand,
	handValue,
	status,
	isClearing,
	isBanker = false,
	align = 'center',
}: {
	hand: BaccaratPlayingCard[]
	handValue: number
	status: BaccaratRoundStatus
	isClearing: boolean
	isBanker?: boolean
	align?: 'left' | 'center' | 'right'
}) {
	const settledIndicesRef = useRef<Set<number>>(new Set())
	const enteringRef = useRef<Record<number, number>>({})
	const [, bumpEnterVersion] = useState(0)

	if (hand.length === 0) {
		if (settledIndicesRef.current.size > 0 || Object.keys(enteringRef.current).length > 0) {
			settledIndicesRef.current = new Set()
			enteringRef.current = {}
		}
	} else {
		for (const index of [...settledIndicesRef.current]) {
			if (index >= hand.length) {
				settledIndicesRef.current.delete(index)
			}
		}

		const freshIndices: number[] = []
		for (let index = 0; index < hand.length; index += 1) {
			if (!settledIndicesRef.current.has(index) && !(index in enteringRef.current)) {
				freshIndices.push(index)
			}
		}

		if (freshIndices.length > 0) {
			const nextEntering = { ...enteringRef.current }
			const baseOrder = Object.keys(nextEntering).length
			freshIndices.forEach((index, order) => {
				nextEntering[index] = (baseOrder + order) * DEAL_STAGGER_MS
			})
			enteringRef.current = nextEntering
		}
	}

	const enteringByIndex = enteringRef.current

	const markEntered = (index: number) => {
		if (settledIndicesRef.current.has(index) && !(index in enteringRef.current)) {
			return
		}

		settledIndicesRef.current.add(index)
		if (!(index in enteringRef.current)) {
			return
		}

		const { [index]: _removed, ...rest } = enteringRef.current
		enteringRef.current = rest
		bumpEnterVersion((version) => version + 1)
	}

	let outlineClass = 'outline-none'
	let badgeClass = cn(
		handValue || hand.length ? 'flex' : 'hidden',
		'absolute items-center justify-center rounded-full px-1.5 text-[10px] font-semibold leading-none tracking-wide',
		'h-5 min-w-5 shadow-sm backdrop-blur-[2px] md:h-6 md:min-w-6 md:px-2 md:text-xs',
	)

	switch (status) {
		case 'win':
			outlineClass = ' -outline-offset-1 outline-emerald-400'
			badgeClass = cn(badgeClass, 'bg-emerald-500/95 text-white')
			break
		case 'lost':
			outlineClass = ' -outline-offset-1 outline-destructive'
			badgeClass = cn(badgeClass, 'bg-destructive/95 text-destructive-foreground')
			break
		case 'draw':
			outlineClass = ' -outline-offset-1 outline-amber-500'
			badgeClass = cn(badgeClass, 'bg-amber-500/95 text-white')
			break
		default:
			badgeClass = cn(badgeClass, 'bg-white/90 text-zinc-800')
	}

	const right = align === 'left' ? 28 : align === 'right' ? 72 : 50
	const badgeReady = hand.length > 0 && Object.keys(enteringByIndex).length === 0

	return (
		<>
			{hand.map((card, index) => {
				const entering = index in enteringByIndex
				const style: CSSProperties = {
					right: `${right}%`,
					top: '120%',
					transform: `translate(${
						isClearing ? 30 - 60 * ((hand.length + 1) / 2 - (index + 1)) : 50 - 60 * ((hand.length + 1) / 2 - (index + 1))
					}%,${isClearing ? -20 - 8 * (hand.length - (index + 1)) : -40 - 8 * (hand.length - (index + 1))}%)`,
					opacity: isClearing ? 0 : 1,
					transitionDelay: isClearing ? `${index * 0.2}s` : '0s',
					zIndex: entering ? 30 : index + 1,
				}

				return (
					<PlayingCardView
						key={`hand-card-${index}`}
						card={card}
						className={outlineClass}
						style={style}
						entering={entering}
						enterDelayMs={enteringByIndex[index] ?? 0}
						isBanker={isBanker}
						onEntered={() => markEntered(index)}
					/>
				)
			})}

			<div
				className={badgeClass}
				style={{
					right: `${right}%`,
					top: '120%',
					zIndex: 25,
					transition: 'opacity 0.45s ease',
					transform: 'translate(50%, calc(-100% - 6.75rem))',
					opacity: isClearing || !badgeReady ? 0 : 1,
					transitionDelay: isClearing ? `${(hand.length - 1) * 0.2}s` : '0.15s',
				}}
			>
				{handValue}
			</div>
		</>
	)
}

function PlacedChips({ chips }: { chips: BaccaratPlacedChip[] }) {
	return (
		<div className='absolute left-1/2 top-1/2 z-10'>
			<div className='relative'>
				{chips.map((chip) => (
					<div
						key={chip.id}
						className='h-0 w-0'
						style={{
							translate: `${chip.offsetX}px ${chip.offsetY}px`,
							transform: `rotateZ(${chip.rotation}deg)`,
						}}
					>
						<Chip chipIndex={chip.chipIndex} size={CHIP_DISPLAY_SIZE} className='-translate-x-1/2 -translate-y-1/2' />
					</div>
				))}
			</div>
		</div>
	)
}

function BettingSpots({
	chips,
	winningPlaces,
	betResults,
	highlightActive,
	disabled,
	onChoosePlace,
}: {
	chips: BaccaratPlacedChip[]
	winningPlaces: BaccaratBetPlace[]
	betResults: BaccaratBetResult[]
	highlightActive: boolean
	disabled: boolean
	onChoosePlace: (place: BaccaratBetPlace) => void
}) {
	const [highlight, setHighlight] = useState(false)
	const winningSet = new Set(winningPlaces)
	const losingSet = new Set(betResults.filter((result) => result.outcome === 'lost').map((result) => result.place))

	useEffect(() => {
		if (!highlightActive || winningPlaces.length === 0) {
			setHighlight(false)
			return
		}

		setHighlight(true)
		const interval = window.setInterval(() => {
			setHighlight((previous) => !previous)
		}, SPOT_FLASH_MS)

		return () => window.clearInterval(interval)
	}, [highlightActive, winningPlaces])

	const getAmount = (place: BaccaratBetPlace) => chips.filter((chip) => chip.place === place).reduce((sum, chip) => sum + chip.amount, 0)

	const isWinning = (place: BaccaratBetPlace) => highlight && winningSet.has(place)

	const isLosing = (place: BaccaratBetPlace) => highlight && losingSet.has(place) && getAmount(place) > 0

	const spotClass = (place: BaccaratBetPlace, tall: boolean) =>
		cn(
			'relative flex flex-col items-center justify-center overflow-hidden rounded-lg border text-foreground shadow-md transition-all duration-200 md:gap-1 md:rounded-2xl',
			tall ? 'h-12 md:h-24' : 'h-9 md:h-16',
			isWinning(place) && 'scale-[1.02] border-primary bg-primary/25 ring-2 ring-primary/40',
			isLosing(place) && 'scale-[1.02] border-destructive bg-destructive/30 ring-2 ring-destructive/50',
			!isWinning(place) && !isLosing(place) && 'border-border/70 bg-card/70 hover:border-primary/50 hover:bg-card/90 active:scale-[0.98]',
			disabled && 'pointer-events-none opacity-70',
		)

	return (
		<div className='mx-auto w-full max-w-3xl px-2 sm:px-3'>
			<div className='grid grid-cols-3 gap-1 md:gap-3'>
				{BETTING_SPOTS.map((spot) => {
					const amount = getAmount(spot.place)

					return (
						<button
							key={spot.place}
							type='button'
							disabled={disabled}
							onClick={() => onChoosePlace(spot.place)}
							className={cn(spotClass(spot.place, spot.tall), spot.className)}
						>
							<span className='text-[8px] font-semibold tracking-wide md:text-xs'>{spot.label}</span>
							<span className={cn('text-[8px] text-muted-foreground md:text-[10px]', spot.tall && 'rounded-full bg-background/50 px-1 py-px md:px-2 md:py-0.5')}>
								x{BACCARAT_PAYOUT_MULTIPLIERS[spot.place]}
							</span>
							<span className={cn('font-medium text-foreground', spot.tall ? 'text-[10px] md:text-sm' : 'text-[10px] md:text-xs')}>{formatBalance(amount, 8)}</span>
							<PlacedChips chips={chips.filter((chip) => chip.place === spot.place)} />
						</button>
					)
				})}
			</div>
		</div>
	)
}

interface BaccaratTableProps {
	playerHand: BaccaratPlayingCard[]
	bankerHand: BaccaratPlayingCard[]
	playerScore: number
	bankerScore: number
	pendingChips: BaccaratPlacedChip[]
	winner: BaccaratHandWinner | null
	winningPlaces: BaccaratBetPlace[]
	betResults: BaccaratBetResult[]
	status: BaccaratRoundStatus
	isClearing: boolean
	isSubmitting: boolean
	isAuthenticated: boolean
	isSpotHighlighting: boolean
	isResultVisible: boolean
	multiplier: number
	profit: number
	currencyCode: string
	onChoosePlace: (place: BaccaratBetPlace) => void
	onUndo: () => void
	onClear: () => void
	onDismissResult: () => void
}

export function BaccaratTable({
	playerHand,
	bankerHand,
	playerScore,
	bankerScore,
	pendingChips,
	winner,
	winningPlaces,
	betResults,
	status,
	isClearing,
	isSubmitting,
	isAuthenticated,
	isSpotHighlighting,
	isResultVisible,
	multiplier,
	profit,
	currencyCode,
	onChoosePlace,
	onUndo,
	onClear,
	onDismissResult,
}: BaccaratTableProps) {
	const bettingLocked = !isAuthenticated || isSubmitting || isClearing
	const canEditBets = isAuthenticated && !isSubmitting && !isClearing && pendingChips.length > 0

	const settled = status === 'win' || status === 'lost' || status === 'draw'
	const playerHandStatus = !settled ? 'idle' : winner === 'Player' ? 'win' : winner === 'Tie' ? 'draw' : 'lost'
	const bankerHandStatus = !settled ? 'idle' : winner === 'Banker' ? 'win' : winner === 'Tie' ? 'draw' : 'lost'

	return (
		<div
			className='relative flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl bg-cover bg-center bg-no-repeat'
			style={{ backgroundImage: `url(${AssetManager.getImageUrl('tableFelt')})` }}
		>
			<ResultModal visible={isResultVisible} multiplier={multiplier} profit={profit} currencyCode={currencyCode} onDismiss={onDismissResult} />

			<DeckStack />

			<div className='min-h-0 flex-1'>
				<div className='grid h-full w-full grid-rows-6 gap-1'>
					<div className='row-span-1 w-full' />
					<div className='relative row-span-2 w-full'>
						<p className='absolute left-0 top-0 z-10 w-1/2 text-center text-[10px] font-semibold uppercase tracking-wide text-white/70 md:text-xs'>Player</p>
						<p className='absolute right-0 top-0 z-10 w-1/2 text-center text-[10px] font-semibold uppercase tracking-wide text-white/70 md:text-xs'>Banker</p>
						<HandPanel hand={playerHand} handValue={playerScore} status={playerHandStatus} isClearing={isClearing} align='right' />
						<HandPanel hand={bankerHand} handValue={bankerScore} status={bankerHandStatus} isClearing={isClearing} isBanker align='left' />
					</div>
					<div className='row-span-3 w-full' />
				</div>
			</div>

			<div className='relative z-10 shrink-0 bg-gradient-to-t from-background/80 via-background/40 to-transparent pt-1.5 md:pt-4'>
				<BettingSpots
					chips={pendingChips}
					winningPlaces={winningPlaces}
					betResults={betResults}
					highlightActive={isSpotHighlighting}
					disabled={bettingLocked}
					onChoosePlace={onChoosePlace}
				/>

				<div className='mx-auto flex w-full max-w-3xl justify-between gap-1.5 px-2 pb-1.5 pt-1.5 sm:px-3 md:gap-2 md:px-3 md:pb-3 md:pt-2.5'>
					<button
						type='button'
						disabled={!canEditBets}
						onClick={onUndo}
						className='inline-flex items-center gap-1 rounded-md border border-foreground/20 bg-background/40 px-2 py-1 text-[11px] font-semibold text-foreground shadow-[0_4px_14px_hsl(var(--background)/0.25)] backdrop-blur-md transition hover:border-foreground/35 hover:bg-background/55 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-40 md:gap-2 md:rounded-xl md:px-3.5 md:py-2 md:text-sm'
					>
						<Undo2 className='h-3 w-3 md:h-4 md:w-4' aria-hidden />
						Undo
					</button>
					<button
						type='button'
						disabled={!canEditBets}
						onClick={onClear}
						className='inline-flex items-center gap-1 rounded-md border border-foreground/20 bg-background/40 px-2 py-1 text-[11px] font-semibold text-foreground shadow-[0_4px_14px_hsl(var(--background)/0.25)] backdrop-blur-md transition hover:border-foreground/35 hover:bg-background/55 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-40 md:gap-2 md:rounded-xl md:px-3.5 md:py-2 md:text-sm'
					>
						<Trash2 className='h-3 w-3 md:h-4 md:w-4' aria-hidden />
						Clear
					</button>
				</div>
			</div>
		</div>
	)
}
